import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { InputMode } from './InputModeToggle';

const SAMPLE_TITLE = 'Friends · S04E03';
const SAMPLE_TEXT =
  'Listen, I know things have been weird lately, but I really think we should grab a coffee and talk it through. You always manage to make everything sound reasonable, even when it absolutely is not.';

const slivWord = (n: number) => {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return 'слово';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'слова';
  return 'слів';
};

interface ModeTileProps {
  active?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  hint: string;
  soon?: boolean;
  onClick?: () => void;
}

const ModeTile: React.FC<ModeTileProps> = ({
  active,
  disabled,
  icon,
  title,
  hint,
  soon,
  onClick,
}) => {
  const theme = useTheme();
  const activeTint =
    theme.palette.mode === 'dark'
      ? 'rgba(155,124,255,0.10)'
      : 'rgba(59,44,147,0.05)';
  const iconBgActive =
    theme.palette.mode === 'dark'
      ? 'rgba(155,124,255,0.18)'
      : 'rgba(59,44,147,0.10)';

  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        bgcolor: active ? activeTint : 'background.paper',
        border: '1px solid',
        borderColor: active ? 'primary.main' : 'primary.tableBorder',
        borderRadius: 2,
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex',
        flex: 1,
        gap: 1.5,
        minWidth: 0,
        opacity: disabled ? 0.62 : 1,
        p: '14px 16px',
        position: 'relative',
        transition: 'all .14s ease',
        '&:hover': disabled
          ? undefined
          : { borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: active ? iconBgActive : 'background.default',
          borderRadius: 1.25,
          color: active ? 'primary.main' : 'text.secondary',
          display: 'inline-flex',
          flex: 'none',
          height: 38,
          justifyContent: 'center',
          width: 38,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction={'row'} alignItems={'center'} spacing={1}>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {title}
          </Typography>
          {soon && (
            <Box
              sx={{
                bgcolor: (t) =>
                  t.palette.mode === 'light'
                    ? 'rgba(59,44,147,0.10)'
                    : 'rgba(155,124,255,0.18)',
                borderRadius: 999,
                color: 'primary.main',
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.04em',
                px: 0.9,
                py: '2px',
                textTransform: 'uppercase',
              }}
            >
              {'скоро'}
            </Box>
          )}
        </Stack>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 12.5,
            lineHeight: 1.4,
            mt: 0.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {hint}
        </Typography>
      </Box>
    </Box>
  );
};

interface IAInputCardProps {
  mode: InputMode;
  onModeChange: (mode: InputMode) => void;
  text: string;
  onTextChange: (text: string) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  analyzing: boolean;
  onAnalyze: () => void;
  canAnalyze: boolean;
}

const IAInputCard: React.FC<IAInputCardProps> = ({
  mode,
  onModeChange,
  text,
  onTextChange,
  file,
  onFileChange,
  analyzing,
  onAnalyze,
  canAnalyze,
}) => {
  const [dragOver, setDragOver] = useState(false);

  const wordsCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    onFileChange(selected);
    if (selected) onModeChange('file');
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const selected = event.dataTransfer.files?.[0] || null;
    if (selected) {
      onFileChange(selected);
      onModeChange('file');
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        boxShadow: '0 14px 36px -28px rgba(20,20,20,0.4)',
        p: { xs: 2, md: '22px 24px' },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mb: 2.25 }}
      >
        <ModeTile
          active={mode === 'text'}
          icon={<TextSnippetOutlinedIcon />}
          title={'Вставити текст'}
          hint={'Транскрипт, стаття або уривок книги'}
          onClick={() => onModeChange('text')}
        />
        <ModeTile
          active={mode === 'file'}
          icon={<DescriptionOutlinedIcon />}
          title={'Файл .txt'}
          hint={'Завантаж локальний файл, до 1 MB'}
          onClick={() => onModeChange('file')}
        />
        <ModeTile
          disabled
          soon
          icon={<LinkOutlinedIcon />}
          title={'YouTube / URL'}
          hint={'Витягне субтитри з відео чи статті'}
        />
      </Stack>

      {mode === 'text' ? (
        <Box>
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={10}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder={
              'Встав сюди текст — напр. транскрипт серії, статтю чи уривок з книги…'
            }
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: 'background.default',
                fontSize: 15,
                lineHeight: 1.5,
              },
              '& .MuiInputBase-inputMultiline': {
                overflow: 'auto !important',
              },
            }}
          />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent={'space-between'}
            spacing={1.25}
            sx={{ mt: 1.25 }}
          >
            <Button
              onClick={() => onTextChange(SAMPLE_TEXT)}
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
              sx={{
                color: 'primary.main',
                fontSize: 14,
                fontWeight: 600,
                px: 0,
                textTransform: 'none',
                '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
              }}
            >
              {`Спробувати на прикладі (${SAMPLE_TITLE})`}
            </Button>
            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
              {wordsCount
                ? `${wordsCount.toLocaleString('uk-UA')} ${slivWord(wordsCount)}`
                : 'Порада: 100–1000 слів — оптимально'}
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={(theme) => ({
            alignItems: 'center',
            bgcolor: dragOver
              ? theme.palette.mode === 'dark'
                ? 'rgba(155,124,255,0.15)'
                : 'rgba(59,44,147,0.07)'
              : theme.palette.mode === 'dark'
              ? 'rgba(155,124,255,0.06)'
              : 'rgba(59,44,147,0.03)',
            border: '1px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: 3,
            py: 4.5,
            textAlign: 'center',
            transition: 'background-color .14s ease',
          })}
        >
          <CloudUploadOutlinedIcon
            sx={{ color: 'primary.main', fontSize: 38 }}
          />
          {file ? (
            <>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                {file.name}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                {`${Math.round(file.size / 1024)} KB`}
              </Typography>
              <Button
                onClick={() => onFileChange(null)}
                size={'small'}
                variant={'text'}
                sx={{ mt: 0.5 }}
              >
                {'Прибрати файл'}
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                {'Перетягни файл сюди або обери на диску'}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                {'Підтримується .txt до 1 MB'}
              </Typography>
              <Button
                component={'label'}
                variant={'outlined'}
                size={'small'}
                startIcon={<FolderOpenOutlinedIcon />}
                sx={{ mt: 1 }}
              >
                {'Обрати файл'}
                <input
                  hidden
                  type={'file'}
                  accept={'.txt,text/plain'}
                  onChange={handleFileInput}
                />
              </Button>
            </>
          )}
        </Box>
      )}

      <Stack direction={'row'} justifyContent={'flex-end'} sx={{ mt: 2.25 }}>
        <Button
          loading={analyzing}
          disabled={!canAnalyze}
          onClick={onAnalyze}
          variant={'contained'}
          startIcon={<AutoFixHighOutlinedIcon />}
          sx={{ height: 44, px: 2.5 }}
        >
          {'Проаналізувати'}
        </Button>
      </Stack>
    </Box>
  );
};

export default IAInputCard;
